import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { CheckCircle, XCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';
import LoadingSpinner from '../components/LoadingSpinner';

const APP_CALLBACK_URL = 'app.gettilted.tilted://login-callback';

function readLinkParams() {
  const query = new URLSearchParams(window.location.search);
  const hash = new URLSearchParams(window.location.hash.slice(1));
  const rawError = query.get('error_description') || hash.get('error_description')
    || query.get('error') || hash.get('error');
  return {
    code: query.get('code'),
    linkError: rawError ? decodeURIComponent(rawError.replace(/\+/g, ' ')) : null,
  };
}

function AuthCallback() {
  const navigate = useNavigate();
  // Read before supabase-js strips the code from the URL
  const [{ code, linkError }] = useState(readLinkParams);
  const [status, setStatus] = useState(linkError ? 'error' : 'loading');
  const isNative = Capacitor.isNativePlatform();

  useEffect(() => {
    if (linkError) return undefined;
    let recoveryDetected = false;

    // Listen for PASSWORD_RECOVERY — if a reset code lands here,
    // redirect to the reset page instead of auto-logging in
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        recoveryDetected = true;
        navigate('/reset-password', { replace: true });
      }
    });

    const handleCallback = async () => {
      try {
        if (isNative && code) {
          // Opened via Universal Link; native has detectSessionInUrl off
          await supabase.auth.exchangeCodeForSession(code).catch((err) => {
            console.warn('Auth callback code exchange failed:', err);
          });
        } else {
          // Wait for detectSessionInUrl to process and fire events
          await new Promise(r => setTimeout(r, 1500));
        }
        if (recoveryDetected) return;

        const { data: { session } } = await supabase.auth.getSession();

        if (session) {
          const redirectPath = localStorage.getItem('auth_redirect');
          localStorage.removeItem('auth_redirect');
          navigate(redirectPath || '/', { replace: true });
        } else if (code) {
          // Email confirmed, but the sign-in code belongs to another browser or the app
          setStatus('confirmed');
        } else {
          navigate('/auth', { replace: true });
        }
      } catch (error) {
        console.error('Auth callback error:', error);
        setStatus(code ? 'confirmed' : 'error');
      }
    };

    handleCallback();

    return () => subscription.unsubscribe();
  }, [navigate, code, linkError, isNative]);

  if (status === 'loading') return <LoadingSpinner />;

  const confirmed = status === 'confirmed';

  return (
    <div
      className="min-h-screen bg-dive-black flex items-center justify-center px-6"
      style={{ paddingTop: 'env(safe-area-inset-top)' }}
    >
      <div className="w-full max-w-sm text-center">
        <img src="/logo-nameplate.png" alt="Tilted" className="h-12 w-auto mx-auto mb-8" />

        <div className="bg-dive-dark border border-dive-gray rounded-2xl p-6">
          {confirmed ? (
            <CheckCircle className="w-14 h-14 text-dive-green mx-auto mb-4" />
          ) : (
            <XCircle className="w-14 h-14 text-dive-neon mx-auto mb-4" />
          )}

          <h1 className="font-display text-3xl text-white tracking-wide mb-2">
            {confirmed ? 'Email Confirmed' : "This Link Didn't Work"}
          </h1>

          <p className="text-gray-400 text-sm mb-6">
            {confirmed
              ? 'Your Tilted account is ready.'
              : `${(linkError || 'Something went wrong').replace(/\.?$/, '.')} Links expire after a while, so try signing in or request a new one.`}
          </p>

          {confirmed && !isNative && (
            <a
              href={`${APP_CALLBACK_URL}?code=${encodeURIComponent(code)}`}
              className="btn-primary block w-full mb-3"
            >
              Open Tilted
            </a>
          )}

          <Link
            to="/auth"
            replace
            className={`${confirmed && !isNative ? 'btn-secondary' : 'btn-primary'} block w-full`}
          >
            {confirmed && !isNative ? 'Sign in on the web' : 'Go to sign in'}
          </Link>

          {confirmed && !isNative && (
            <p className="text-gray-500 text-xs mt-4">
              If the app doesn't open, go back to Tilted and sign in with your email and password.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export default AuthCallback;
